/* eslint-disable class-methods-use-this */
import Balance from '@core/models/user/Balance';
import { limitsConsentICoreName } from '@core/services/authentication/_private/consts';
import { UserService } from '@core/services/user/UserService';
import User from '@models/user/User';
import { shellLocalConfig } from '@src/config/shell/localConfig';
import { authenticationService } from '@core/services/authentication/AuthenticationService';
import { getAccountConfig } from '@accountFederation/config/accountConfig';
import { KycDetails } from '@accountFederation/modules/idCardVerification/getid/types';
import { resetKycStatusViewed } from '@accountFederation/modules/idCardVerification/getid/isStatusViewed';
import store from '@app/store';

export class UserDataService {
    public async getUserData(user: User): Promise<User> {
        let newUser = user;

        // Start async retries without blocking the flow
        this.retryAsync(() => this.updateBalance());
        this.retryAsync(() => this.updateKycDetails(user, newUser));

        const [limitsConsent, detailsAfterMfa] = await Promise.all([
            this.updateLimitsConsent(),
            this.updateDetailsAfterMfa(user),
        ]);

        newUser = { ...newUser, ...limitsConsent, ...detailsAfterMfa };

        return newUser;
    }

    private retryAsync<T>(fn: () => Promise<T>, retries = 2, delay = 2000): void {
        const attempt = async (remainingRetries: number) => {
            try {
                await fn();
            } catch (error) {
                if ((error as any)?.response?.status !== 401) {
                    console.error(`Retry attempt failed, remaining retries: ${remainingRetries}`, error);
                }
                if (remainingRetries > 0) {
                    setTimeout(() => attempt(remainingRetries - 1), delay);
                }
            }
        };
        attempt(retries);
    }

    private async updateBalance(): Promise<void> {
        if (!shellLocalConfig.hasBalance) {
            return;
        }

        const response = await this.getBalance();
        store.dispatch(
            'data/authentication/setUserData',
            {
                key: 'balance',
                value: response.balance,
            },
            {
                root: true,
            },
        );

        // Check active bonus in order to show user correct bonus data and do correct bonus calculations
        if (response.balance) {
            store.dispatch('ui/user/bonuses/checkActiveBonuses', null, { root: true });
        }
    }

    private async updateLimitsConsent(): Promise<Partial<GetLimitsConsentResult>> {
        if (shellLocalConfig.hasLimitsConsent) {
            try {
                return await this.getLimitsConsent();
            } catch (error) {
                if ((error as any)?.response?.status !== 401) {
                    console.error('Error fetching limits consent:', error);
                }
            }
        }
        return {};
    }

    private async updateKycDetails(originalUser: User, user: User): Promise<void> {
        if (getAccountConfig().isCanonKycEnabled) {
            resetKycStatusViewed(originalUser.kycDetails?.status, user.kycDetails?.status);
            const response = await this.getKycDetails();
            store.dispatch(
                'data/authentication/setUserData',
                {
                    key: 'kycDetails',
                    value: response.kycDetails,
                },
                {
                    root: true,
                },
            );
        }
    }

    private async updateDetailsAfterMfa(originalUser: User): Promise<Partial<User>> {
        if (originalUser.mfaPayload) {
            try {
                return await this.getDetailsAfterMfa();
            } catch (error) {
                if ((error as any)?.response?.status !== 401) {
                    console.error('Error fetching details after MFA:', error);
                }
            }
        }
        return {};
    }

    private async getLimitsConsent(): Promise<GetLimitsConsentResult> {
        const response = await UserService.getInstance().getPlayerSettings(limitsConsentICoreName);
        return { limitsConsent: response.data.SettingValue };
    }

    private async getBalance(): Promise<GetBalanceResult> {
        return { balance: await UserService.getInstance().getPlayerBalance() };
    }

    private async getKycDetails(user?: User): Promise<GetKycDetailsResult> {
        return { kycDetails: await authenticationService.getKycDetails(user) };
    }

    private async getDetailsAfterMfa(): Promise<Partial<User>> {
        return await UserService.getInstance().getUserDetails();
    }
}

type GetBalanceResult = {
    balance: Balance | null;
};

type GetKycDetailsResult = {
    kycDetails?: KycDetails | undefined;
};
type GetLimitsConsentResult = {
    limitsConsent: boolean;
};

export default new UserDataService();
